import React, { useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  IconButton,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
  useTheme,
} from '@mui/material';
import {
  Inventory as InventoryIcon,
  LocalShipping as ReceiveIcon,
  InfoOutlined as HelpIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { relativeDate } from '../../../../utils/dateUtils';
import { useVenue } from '../../../../context/VenueContext';
import inventoryDashboardService from '../../../../services/inventoryDashboardService';
import MovementTypeBadge from '../../../../components/venue/MovementTypeBadge';
// ── Help modal — remove the next import + all helpOpen state + <InventoryHelpModal> to disable ──
import InventoryHelpModal from '../../../../components/venue/InventoryHelpModal';
import type { InventoryDashboard as InventoryDashboardData, DashboardMovementRow } from '../../../../types/venue';

// ── Helpers ───────────────────────────────────────────────────────────────────

function signedQty(qty: number): string {
  return qty > 0 ? `+${qty}` : String(qty);
}

function qtyColor(qty: number): string {
  if (qty > 0) return '#1a7f37';
  if (qty < 0) return '#cf222e';
  return '#6e7781'; // neutral — matches text.secondary in both themes
}

function fmtBottleSize(ml: number | null | undefined): string {
  if (ml == null) return '';
  return ml >= 1000 ? `${ml / 1000}L` : `${ml}ml`;
}

// ── Stat card ─────────────────────────────────────────────────────────────────

interface StatCardProps {
  label: string;
  value: number | null;
  color?: string;
  onClick?: () => void;
}

const StatCard: React.FC<StatCardProps> = ({ label, value, color, onClick }) => {
  const theme = useTheme();
  const isClickable = !!onClick;
  return (
    <Paper
      onClick={onClick}
      sx={{
        p: 2.5,
        borderRadius: 2,
        boxShadow: '0 4px 12px rgba(0,0,0,0.06)',
        flex: '1 1 160px',
        minWidth: 140,
        cursor: isClickable ? 'pointer' : 'default',
        transition: isClickable ? 'box-shadow 0.15s, transform 0.1s' : undefined,
        '&:hover': isClickable ? {
          boxShadow: '0 6px 18px rgba(0,0,0,0.12)',
          transform: 'translateY(-1px)',
        } : undefined,
      }}
    >
      <Typography
        variant="caption"
        fontWeight={600}
        sx={{
          color: theme.palette.text.secondary,
          textTransform: 'uppercase',
          letterSpacing: '0.05em',
          display: 'block',
          mb: 1,
        }}
      >
        {label}
      </Typography>
      <Typography
        variant="h4"
        fontWeight={700}
        sx={{ color: color ?? theme.palette.text.primary }}
      >
        {value ?? '—'}
      </Typography>
    </Paper>
  );
};

// ── Section header ────────────────────────────────────────────────────────────

interface SectionHeaderProps {
  icon: React.ReactNode;
  title: string;
  badge?: number | null;
  action?: React.ReactNode;
}

const SectionHeader: React.FC<SectionHeaderProps> = ({ icon, title, badge, action }) => {
  const theme = useTheme();
  return (
    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1.5 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <Box sx={{ color: theme.palette.text.secondary, display: 'flex' }}>{icon}</Box>
        <Typography variant="subtitle1" fontWeight={600}>{title}</Typography>
        {badge != null && (
          <Typography variant="caption" color="text.secondary">({badge})</Typography>
        )}
      </Box>
      {action}
    </Box>
  );
};

// ── Component ─────────────────────────────────────────────────────────────────

const InventoryDashboard: React.FC = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const { venueId } = useVenue();

  const [data, setData] = useState<InventoryDashboardData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [helpOpen, setHelpOpen] = useState(false); // ← remove with InventoryHelpModal

  useEffect(() => {
    if (!venueId) return;
    setLoading(true);
    setError(null);
    inventoryDashboardService.getDashboard(venueId)
      .then(setData)
      .catch(() => setError('Failed to load inventory dashboard. Please try again.'))
      .finally(() => setLoading(false));
  }, [venueId]);

  const inventoryBase = `/admin/venues/${venueId}/inventory`;

  // ── Loading ───────────────────────────────────────────────────────────────
  if (loading) {
    return (
      <Box sx={{ p: 3, display: 'flex', justifyContent: 'center', pt: 8 }}>
        <CircularProgress />
      </Box>
    );
  }

  // ── Error ─────────────────────────────────────────────────────────────────
  if (error || !data) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error">{error ?? 'No data available.'}</Alert>
      </Box>
    );
  }

  // ── Helpers ───────────────────────────────────────────────────────────────
  const colStyle = {
    fontWeight: 600,
    color: theme.palette.text.secondary,
    fontSize: '0.72rem',
    textTransform: 'uppercase' as const,
    letterSpacing: '0.04em',
    whiteSpace: 'nowrap' as const,
  };

  const renderMovementRow = (row: DashboardMovementRow, index: number) => {
    return (
      <TableRow key={`${row.inventoryItemId}-${index}`} hover>
        <TableCell>
          <Typography variant="body2" fontWeight={500}>
            {row.displayName || '—'}
          </Typography>
          {row.bottleSize != null && (
            <Typography variant="caption" color="text.secondary">
              {fmtBottleSize(row.bottleSize)}
            </Typography>
          )}
        </TableCell>
        <TableCell>
          <MovementTypeBadge type={row.type} />
        </TableCell>
        <TableCell>
          <Typography
            variant="body2"
            fontWeight={700}
            sx={{ color: qtyColor(row.quantity), fontFamily: 'monospace' }}
          >
            {signedQty(row.quantity)}
          </Typography>
        </TableCell>
        <TableCell>
          <Typography variant="body2" color="text.secondary">
            {row.locationName ?? '—'}
          </Typography>
        </TableCell>
        <TableCell>
          <Typography variant="body2" color="text.secondary" whiteSpace="nowrap">
            {relativeDate(row.postedAt)}
          </Typography>
        </TableCell>
      </TableRow>
    );
  };

  const renderReceiveRow = (row: DashboardMovementRow, index: number) => {
    const sizeAndLocation = [fmtBottleSize(row.bottleSize), row.locationName].filter(Boolean).join(' · ');
    return (
      <TableRow key={`recv-${row.inventoryItemId}-${index}`} hover>
        <TableCell>
          <Typography variant="body2" fontWeight={500}>
            {row.displayName || '—'}
          </Typography>
          {sizeAndLocation && (
            <Typography variant="caption" color="text.secondary">
              {sizeAndLocation}
            </Typography>
          )}
        </TableCell>
        <TableCell>
          <Typography variant="body2" fontWeight={600} sx={{ color: '#1a7f37' }}>
            +{row.quantity}
          </Typography>
        </TableCell>
        <TableCell>
          <Typography variant="body2" color="text.secondary" whiteSpace="nowrap">
            {relativeDate(row.postedAt)}
          </Typography>
        </TableCell>
      </TableRow>
    );
  };

  // ── Render ────────────────────────────────────────────────────────────────
  return (
    <>
    <Box sx={{ p: 3, display: 'flex', flexDirection: 'column', gap: 3 }}>

      {/* ── Page header ──────────────────────────────────────────────── */}
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <InventoryIcon sx={{ color: theme.palette.primary.main }} />
          <Typography variant="h6" fontWeight={600}>Inventory Overview</Typography>
          <Tooltip title="How to use this page" placement="right">
            <IconButton
              size="small"
              onClick={() => setHelpOpen(true)}
              sx={{
                color: theme.palette.text.disabled,
                '&:hover': { color: theme.palette.primary.main },
                ml: 0.25,
              }}
            >
              <HelpIcon sx={{ fontSize: 17 }} />
            </IconButton>
          </Tooltip>
        </Box>
        <Button
          size="small"
          onClick={() => navigate(`${inventoryBase}/valuation`)}
          sx={{ textTransform: 'none', fontWeight: 600, color: 'text.secondary' }}
        >
          Stock estimate
        </Button>
      </Box>

      {/* ── Stat cards ─────────────────────────────────────────────────── */}
      <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
        <StatCard label="Total Items" value={data.totalItems} />
        <StatCard label="Total Bottles" value={data.totalBottles} />
        <StatCard
          label="Low Stock"
          value={data.lowStockCount}
          color={data.lowStockCount > 0 ? '#9a6700' : undefined}
          onClick={() => navigate(`${inventoryBase}?status=LOW`)}
        />
        <StatCard
          label="Out of Stock"
          value={data.outOfStockCount}
          color={data.outOfStockCount > 0 ? '#cf222e' : undefined}
          onClick={() => navigate(`${inventoryBase}?status=OUT`)}
        />
      </Box>

      {/* ── Received last 7 days ───────────────────────────────────────── */}
      <Box>
        <SectionHeader
          icon={<ReceiveIcon fontSize="small" />}
          title="Received last 7 days"
          badge={data.recentlyReceived.count}
          action={
            <Button
              size="small"
              onClick={() => navigate(`${inventoryBase}/movements?type=RECEIVE`)}
              sx={{ textTransform: 'none', fontWeight: 600, color: 'text.secondary' }}
            >
              View all
            </Button>
          }
        />
        <Paper sx={{ borderRadius: 2, overflow: 'hidden', boxShadow: '0 4px 12px rgba(0,0,0,0.06)' }}>
          {data.recentlyReceived.items.length === 0 ? (
            <Box sx={{ py: 4, textAlign: 'center', color: theme.palette.text.secondary }}>
              <Typography variant="body2">No deliveries received in the last 7 days.</Typography>
            </Box>
          ) : (
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    {['Wine', 'Qty', 'Date'].map(h => (
                      <TableCell key={h} sx={colStyle}>{h}</TableCell>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {data.recentlyReceived.items.map(renderReceiveRow)}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </Paper>
      </Box>

      {/* ── Recent activity ────────────────────────────────────────────── */}
      <Box>
        <SectionHeader
          icon={<InventoryIcon fontSize="small" />}
          title="Recent activity"
          badge={data.recentMovements.length > 0 ? data.recentMovements.length : null}
          action={
            <Button
              size="small"
              onClick={() => navigate(`${inventoryBase}/movements`)}
              sx={{ textTransform: 'none', fontWeight: 600, color: 'text.secondary' }}
            >
              View all
            </Button>
          }
        />
        <Paper sx={{ borderRadius: 2, overflow: 'hidden', boxShadow: '0 4px 12px rgba(0,0,0,0.06)' }}>
          {data.recentMovements.length === 0 ? (
            <Box sx={{ py: 4, textAlign: 'center', color: theme.palette.text.secondary }}>
              <Typography variant="body2">No movements recorded yet.</Typography>
            </Box>
          ) : (
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    {['Wine', 'Type', 'Qty', 'Location', 'Date'].map(h => (
                      <TableCell key={h} sx={colStyle}>{h}</TableCell>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {data.recentMovements.map(renderMovementRow)}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </Paper>
      </Box>

    </Box>

    {/* ── Help modal — remove this block + helpOpen state + HelpIcon import to disable ── */}
    <InventoryHelpModal open={helpOpen} onClose={() => setHelpOpen(false)} />
    </>
  );
};

export default InventoryDashboard;
